import clsx from 'clsx';

import { default as PAReactTree } from 'pa-react/dist/components/tree/Tree';
import PaginatedTreeDataProvider from 'pa-react/dist/core/dataProviders/PaginatedTreeDataProvider';
import TreeDataProvider from 'pa-react/dist/core/dataProviders/TreeDataProvider';

import './Tree.css';

//
// --- Types ---

/** @see: https://github.com/bvaughn/react-virtualized/blob/master/source/List/types.js */
export type ReactVirtualizedListRenderedRows = {
  overscanStartIndex: number;
  overscanStopIndex: number;
  startIndex: number;
  stopIndex: number;
};

export interface PaginatedTreeDataProviderFetchDataOptions {
  pageNumber?: number;
  pageSize?: number;
}

// @see https://gitlab.poweradvocate.com/information-technology/developers/js-repo/pa-react-monorepo/-/blob/master/packages/pa-react/src/lib/core/dataProviders/PaginatedTreeDataProvider.js
export interface PaginatedTreeDataProviderFetchDataNode {
  /** what the node is meant to represent given to us by the user */
  data: any;
  /** flatIndex - treeIndex (the offset used to find this nodes "flatIndex") */
  indexOffset: number;
  /** how many children this node has */
  length: number;
  /** how many extra nodes this node is responsible for displaying */
  lengthOffset: number;
  /** how deep this node is */
  nodeDepth: number;
  /** a list of all child nodes that are open sorted by treeIndex */
  openNodes: ReadonlyArray<number>;
  /** the parent of this node */
  parent: PaginatedTreeDataProviderFetchDataNode;
  /** the index of a node compared to his siblings in his parents branch */
  treeIndex: number;
}

interface PAReactTreeProps {
  className?: string;
  height?: number;
  nodeHeight?: number | ((node: any, s: any) => number);
  onNodeToggle?: (nodeData: any, node: any) => void;
  onNodeClick?: (nodeData: any, node: any) => void;
  onRowsRendered?: (rowsRenderedInfo: ReactVirtualizedListRenderedRows) => void;
  onSetLength?: (nodeLength: number, opts?: TreeSetLengthOpts) => void;
  scrollToIndex?: number;
  treeDataProvider: InstanceType<typeof TreeDataProvider>;
  width?: number;
}

export interface TreeProps extends PAReactTreeProps {
  className?: string;
}

export type TreeSetLengthOpts = {
  firstLoad?: boolean;
  firstSetLength?: boolean;
};

export type PaginatedTreeDataProviderFetchData<T = any> = (
  opts: PaginatedTreeDataProviderFetchDataOptions
) => Promise<T>;

//
// --- Tree Component ---

// NOTE: class name of this component breaks projects standards. `.tree` would be preferred, but that is already
// used by the PA React version and naming the component SupplierSearchTree or BaseTree seemed more problematic
// and would differ from other "Base" components in this app.

const Tree: React.FC<TreeProps> = ({ className, ...remainingTreeProps }) => (
  <PAReactTree className={clsx('supplier-search-tree', className)} {...remainingTreeProps} />
);

export default Tree;

// re-export TreeDataProvider's for easier access
export { PaginatedTreeDataProvider, TreeDataProvider };
